import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Injector, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { ApAlertService } from 'webcommon/core-ui';
import { ProcedureCodeMappingDto } from 'webcommon/shared';
import { FcItemModalContext } from '../../../models';
import { FcItemListBaseComponent, FcListValidationService, FormControlExtended } from '../../generic';

export interface ProcedureMappingListItemDto {
  ProcedureCodeMapping: ProcedureCodeMappingDto[];
}

interface ProcedureMappingFormGroup {
  mapItemUid: FormControl<string>;
  procedureCodePk: FormControl<string[]>;
  substitutionText: FormControl<string>;
}

function procedureCodeUniqueValidator(subject: BehaviorSubject<string[]>): ValidatorFn {
  return (control: FormControl<string[]>): ValidationErrors | null => {
    const controlPkNotUnique = subject.getValue().some((pk) => control.value.includes(pk));
    return controlPkNotUnique ? { isUnique: true } : null;
  };
}

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-procedure-mapping-list',
  templateUrl: './fc-procedure-mapping-list.component.html',
  styleUrls: ['./fc-procedure-mapping-list.component.less'],
})
export class FcProcedureMappingListComponent extends FcItemListBaseComponent implements OnChanges {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: ProcedureMappingListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;

  private procedureCodeNotUniquePksSubject = new BehaviorSubject<string[]>([]);
  public procedureCodeMappingFormArray = new FormArray<FormGroup>([]);

  constructor(injector: Injector) {
    super(injector);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.form != null) {
      this.addControlsToForm();
    }

    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public addControlsToForm(): void {
    this.form.addControl('ProcedureCodeMapping', this.procedureCodeMappingFormArray);
  }

  public setForm(): void {
    this.procedureCodeMappingFormArray.clear();

    if (Array.isArray(this.listItem?.ProcedureCodeMapping)) {
      const items = [...this.listItem.ProcedureCodeMapping];
      items.sort((a, b) => {
        return a.DisplayOrder > b.DisplayOrder ? 1 : -1;
      });
      for (const item of items) {
        this.addProcedureMappingRow(item.FindTableToItemUid, item.SubstitutionText, item.MapItemUid);
      }
    }

    this.addProcedureMappingRow();
  }

  private setNotUniquePks() {
    const uniquePks: string[] = [];
    const notUniquePks: string[] = [];
    for (const item of this.procedureCodeMappingFormArray.value) {
      if (Array.isArray(item?.procedureCodePk)) {
        for (const pk of item.procedureCodePk) {
          if (typeof pk === 'string') {
            if (!uniquePks.includes(pk)) {
              uniquePks.push(pk);
            } else if (!notUniquePks.includes(pk)) {
              notUniquePks.push(pk);
            }
          }
        }
      }
    }
    this.procedureCodeNotUniquePksSubject.next(notUniquePks);
    this.validateProcedureCodeUnique();
  }

  private addProcedureMappingRow(procedureCodePks?: string, substitutionText?: string, mapItemUid?: string): void {
    const procedureCodeControl = new FormControlExtended(
      procedureCodePks ? [procedureCodePks] : [],
      procedureCodeUniqueValidator(this.procedureCodeNotUniquePksSubject)
    );
    procedureCodeControl.showErrorFlag = true;
    this.procedureCodeMappingFormArray.push(
      new FormGroup({
        mapItemUid: new FormControl(mapItemUid ?? null),
        procedureCodePk: procedureCodeControl,
        substitutionText: new FormControl(substitutionText ?? '', [Validators.maxLength(200)]),
      })
    );
  }

  public deleteProcedureMappingRow(index: number): void {
    this.procedureCodeMappingFormArray.removeAt(index);
    this.setNotUniquePks();
    this.validateProcedureCodeUnique();
  }

  private validateProcedureCodeUnique(): void {
    for (const formGroup of this.procedureCodeMappingFormArray.controls) {
      formGroup.get('procedureCodePk')?.updateValueAndValidity();
    }
  }

  public getProcedureCodeControl(index: number): FormControl<string[]> {
    return this.procedureCodeMappingFormArray.at(index).get('procedureCodePk') as FormControl<string[]>;
  }

  public getSubstitutionTextControl(index: number): FormControl<string> {
    return this.procedureCodeMappingFormArray.at(index).get('substitutionText') as FormControl<string>;
  }

  public reorder(event: CdkDragDrop<ProcedureMappingFormGroup>): void {
    const formGroup = this.procedureCodeMappingFormArray.at(event.previousIndex) as FormGroup;
    this.procedureCodeMappingFormArray.removeAt(event.previousIndex);
    this.procedureCodeMappingFormArray.insert(event.currentIndex, formGroup);
  }

  public procedureCodeChanged(): void {
    // Add empty row when every row has Pk
    const isEmptyRow = this.procedureCodeMappingFormArray.value.some((item: any) => {
      return !(typeof item?.procedureCodePk?.[0] === 'string');
    });
    if (!isEmptyRow) {
      this.addProcedureMappingRow();
    }

    this.setNotUniquePks();
  }

  public deleteRowButtonVisible(index: number): boolean {
    const itemsWithoutProcedureCodePk = (this.procedureCodeMappingFormArray.value.filter((item: any) => {
      return !(typeof item?.procedureCodePk?.[0] === 'string');
    })).length;
    const currentItemHasProcedureCodePk = typeof this.procedureCodeMappingFormArray.value[index]?.procedureCodePk?.[0] === 'string';
    if (itemsWithoutProcedureCodePk <= 1) {
      return currentItemHasProcedureCodePk;
    }
    return true;
  }
}
